import * as FileSystem from 'expo-file-system/legacy';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

import {
  type PreparedRecoveryEscrow,
  type RecoveryCaptureStore,
  type RecoveryIdentity,
  type RecoveryPersistedObject,
} from '@/types/recovery';

const INDEX_FILE = 'prepared.json';
const ROOT_DIRECTORY = 'recovery-escrow';

const sha512 = (bytes: Uint8Array): string =>
  naclUtil.encodeBase64(nacl.hash(bytes));

const safeSegment = (value: string): string => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
    throw new Error('Identificador invalido para persistencia do escrow.');
  }
  return value;
};

const recoveryDirectory = (identity: RecoveryIdentity, recoveryId: string): string => {
  if (!FileSystem.documentDirectory) throw new Error('Diretorio persistente indisponivel.');
  return `${FileSystem.documentDirectory}${ROOT_DIRECTORY}/${safeSegment(identity.companyId)}/${safeSegment(identity.userId)}/${safeSegment(identity.deviceId)}/${safeSegment(recoveryId)}/`;
};

const localUriToFileUri = (localUri: string): string => {
  const match = localUri.match(/^recovery-capture:\/\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/(.+)$/);
  if (!match || match[5].includes('..')) throw new Error('URI local do escrow invalida.');
  return `${recoveryDirectory({
    companyId: match[1], userId: match[2], deviceId: match[3],
  }, match[4])}${match[5]}`;
};

const readBase64Bytes = async (uri: string): Promise<Uint8Array | null> => {
  const info = await FileSystem.getInfoAsync(uri);
  if (!info.exists) return null;
  return naclUtil.decodeBase64(await FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  }));
};

export class FileSystemRecoveryCaptureStore implements RecoveryCaptureStore {
  async loadPrepared(
    _identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<PreparedRecoveryEscrow | null> {
    const indexUri = `${recoveryDirectory(_identity, recoveryId)}${INDEX_FILE}`;
    const info = await FileSystem.getInfoAsync(indexUri);
    if (!info.exists) return null;
    return JSON.parse(await FileSystem.readAsStringAsync(indexUri, {
      encoding: FileSystem.EncodingType.UTF8,
    })) as PreparedRecoveryEscrow;
  }

  async readPersistedObject(localUri: string): Promise<Uint8Array | null> {
    return readBase64Bytes(localUriToFileUri(localUri));
  }

  async commitPreparedAtomically(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    objects: readonly RecoveryPersistedObject[];
  }): Promise<void> {
    const finalDirectory = recoveryDirectory(input.identity, input.recoveryId);
    const existing = await FileSystem.getInfoAsync(`${finalDirectory}${INDEX_FILE}`);
    if (existing.exists) return;

    const temporaryDirectory = `${finalDirectory.slice(0, -1)}.tmp/`;
    await FileSystem.deleteAsync(temporaryDirectory, { idempotent: true });
    await FileSystem.makeDirectoryAsync(temporaryDirectory, { intermediates: true });
    try {
      for (const object of input.objects) {
        if (!object.relativePath || object.relativePath.includes('..')) {
          throw new Error('Caminho relativo do escrow invalido.');
        }
        const destination = `${temporaryDirectory}${object.relativePath}`;
        const parent = destination.slice(0, destination.lastIndexOf('/') + 1);
        await FileSystem.makeDirectoryAsync(parent, { intermediates: true });
        await FileSystem.writeAsStringAsync(destination, naclUtil.encodeBase64(object.bytes), {
          encoding: FileSystem.EncodingType.Base64,
        });
        const persisted = await readBase64Bytes(destination);
        if (!persisted || persisted.length !== object.bytes.length || sha512(persisted) !== sha512(object.bytes)) {
          throw new Error('Falha ao confirmar objeto local do escrow.');
        }
      }
      await FileSystem.writeAsStringAsync(
        `${temporaryDirectory}${INDEX_FILE}.tmp`,
        JSON.stringify(input.prepared),
        { encoding: FileSystem.EncodingType.UTF8 }
      );
      await FileSystem.moveAsync({
        from: `${temporaryDirectory}${INDEX_FILE}.tmp`,
        to: `${temporaryDirectory}${INDEX_FILE}`,
      });
      await FileSystem.makeDirectoryAsync(finalDirectory.slice(0, finalDirectory.lastIndexOf('/', finalDirectory.length - 2) + 1), {
        intermediates: true,
      });
      await FileSystem.moveAsync({ from: temporaryDirectory, to: finalDirectory });
    } catch (error) {
      await FileSystem.deleteAsync(temporaryDirectory, { idempotent: true }).catch(() => undefined);
      throw error;
    }
  }
}
